import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_DISTRIBUTORS_QUERY,
    getDistributorsQueryVariables,
} from '../queries/getDistributorsQuery';
import { Distributor } from '../types/Distributor';
import { transformDistributorResponse } from '../tools/transformDistributorResponse';
import { useGeographicRegions } from '../../geographic/contexts/GeographicRegionsProvider';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface DistributorsContextValue {
    distributors: Distributor[];
    distributorById: Record<string, Distributor>;
    loading: boolean;
    error: any;
    // Filtering
    selectedGeographicRegionId: string[] | null;
    setSelectedGeographicRegionId: (id: string[] | null) => void;
    selectedCountryId: string[] | null;
    setSelectedCountryId: (id: string[] | null) => void;
    filteredDistributors: Distributor[];
}

const DistributorsContext = createContext<DistributorsContextValue | undefined>(undefined);

interface DistributorsProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const DistributorsProvider = ({ children, apolloClient }: DistributorsProviderProps) => {
    const { locale } = useLocale();
    const { geographicRegionById } = useGeographicRegions();
    const [selectedGeographicRegionId, setSelectedGeographicRegionId] = useState<string[] | null>(
        null
    );
    const [selectedCountryId, setSelectedCountryId] = useState<string[] | null>(null);

    const query = GET_DISTRIBUTORS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery(query, {
        variables: getDistributorsQueryVariables(locale),
        client: apolloClient,
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized: true, queryError: queryError });

    const distributors = useMemo(() => {
        const source = queryData ?? fallbackData;
        if (!source) return [];
        return transformDistributorResponse(source);
    }, [queryData, fallbackData]);

    const distributorById = useMemo(() => {
        const map: Record<string, Distributor> = {};
        distributors.forEach((d) => {
            map[d.id] = d;
        });
        return map;
    }, [distributors]);

    // Filtering logic:
    // If a geographic region is selected, filter distributors to those that have at least one country in that region.
    // If a country is selected, filter distributors to those that are linked to that country.
    const filteredDistributors = useMemo(() => {
        let result = distributors;

        if (selectedGeographicRegionId && selectedGeographicRegionId.length > 0) {
            const regionCountryIds = selectedGeographicRegionId.flatMap((regionId) => {
                const region = geographicRegionById[regionId];
                return region ? region.countries.map((c) => c.id) : [];
            });
            result = result.filter((d) =>
                d.countryIds.some((countryId) => regionCountryIds.includes(countryId))
            );
        }

        if (selectedCountryId && selectedCountryId.length > 0) {
            result = result.filter((d) =>
                d.countryIds.some((countryId) => selectedCountryId.includes(countryId))
            );
        }

        return result;
    }, [distributors, selectedGeographicRegionId, selectedCountryId, geographicRegionById]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <DistributorsContext.Provider
            value={{
                distributors,
                distributorById,
                loading,
                error,
                selectedGeographicRegionId,
                setSelectedGeographicRegionId,
                selectedCountryId,
                setSelectedCountryId,
                filteredDistributors,
            }}
        >
            {children}
        </DistributorsContext.Provider>
    );
};

function useDistributors() {
    const context = useContext(DistributorsContext);
    if (!context) {
        throw new Error('useDistributors must be used within a DistributorsProvider');
    }
    return context;
}

export { DistributorsProvider, useDistributors };
export type { DistributorsContextValue };
